import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import MeetWv, { MeetCameraRefType } from './meet-wv';
import { MeetCameraBottom } from './layouts/bottom';
import '@/assets/styles/components/meet-camera.scss';
import MeetCameraHeader from './layouts/header.tsx';
import { DialogModal } from '@/components';
import { useSocket } from '@/providers/socket-io';
import { CALL_STATE_TYPES, MEETING_REASON_TYPES, SOCKET_EVENTS, VIDEO_CALL_PROVIDER_NAMES } from '@/constants/socket.ts';
import { useMeeting } from '@/context/meeting-context';
import ClassNames from 'classnames';
import WhiteBoardWv from '@/components/meet-camera/whiteboard-wv';
import LoadingSpinner from '@/components/loading-spinner';
import VideoCallMessages from '@/components/meet-camera/video-call-messages';
import { checkWebView, convertMsToClockTime, isEmptyObject, jsonTryParse, sendGenericEmit } from '@/utility';
import { useServerTimeStore } from '@/zustand/server-time/store.ts';
import { useDevicePermissions } from '@/context/device-permission-context';
import WhiteboardQuestionGallery from '@/components/meet-camera/whiteboard-question-gallery';
import { sendMessageToWebView } from '@/utility/webview.ts';
import { PostMessage } from '@/constants/post-message.ts';
import { TimeSpan } from '@/utility/time-span.ts';
import { useFeatureFlag } from 'configcat-react';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';

type UserInfosType = {
	studentName: string;
	studentAvatar: string;
	coachName: string;
	coachAvatar: string;
	coachSubject: string;
	coachId: string;
};

export type MeetCameraInitProps = {
	streamId: string;
	meetingId: string;
	roomId: string;
	token: string;
	socketURL: string;
	providerName: string;
	interactionId: string;
	endDate: Date;
	extensionNotificationDate: string;
	meetingLastConnectionDate: string;
	userInfos: UserInfosType;
};

export type MeetCameraHandle = {
	init: (props: MeetCameraInitProps) => void;
	onEndDateUpdate: (string) => void;
};

const MeetCamera = forwardRef<MeetCameraHandle, { connectionApp: string; onInitialized: (id: string) => void }>(({ connectionApp, onInitialized }, ref) => {
	const meetWvRef = useRef<MeetCameraRefType>(null);
	const whiteboardRef = useRef<any>(null);
	const [activeMicrophone, setActiveMicrophone] = useState<boolean>(true);
	const [activeVideoCamera, setActiveVideoCamera] = useState<boolean>(true);
	const [viewType, setViewType] = useState<number>(1);
	const [showExtensionModal, setShowExtensionModal] = useState<boolean>(false);
	const [isActiveMessages, setIsActiveMessages] = useState<boolean>(false);
	const [incomingMessages, setIncomingMessages] = useState([]);

	const [issueReportModal, setIssueReportModal] = useState<boolean>(false);
	const [issueOptions, setIssueOptions] = useState([]);
	const [selectedIssueOptions, setSelectedIssueOptions] = useState([]);
	const [connectMeetLinkModal, setConnectMeetingLinkModal] = useState<boolean>(false);
	const [googleMeetProvidedLink, setGoogleMeetProvidedLink] = useState<any>({});
	const [copiedTxtShown, setCopiedTxtShown] = useState<boolean>(false);

	const [showExtensionSubmittedModal, setShowExtensionSubmittedModal] = useState<boolean>(false);
	const [showEndModal, setShowEndModal] = useState<boolean>(false);
	const [activeScreenShare, setActiveScreenShare] = useState(false);
	const [activeMicrophoneDevice, setActiveMicrophoneDevice] = useState('default');
	const { hasPermission, controlPermission } = useDevicePermissions();
	const [isInWebView, setIsInWebView] = useState(false);
	const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

	const { onExtensionSubmitted, callState, onLeaveCall, onShowStudentNotConnect, setMeetingType, socketData } = useMeeting();

	const { meetingCreated, handleMatch } = socketData;
	const { data: serverTimeData } = useServerTimeStore();

	const [isToaster, setIsToaster] = useState<boolean>(false);
	const { emit, on, off, isConnected } = useSocket();

	const [deviceList, setDeviceList] = useState({
		cameras: [],
		microphones: []
	});

	const dopingTechComponentsCacheStrRef = useRef<string>('current');
	const { value: dopingTechComponentsCacheStr } = useFeatureFlag(CONFIGCAT_KEYS.DOPING_TECH_COMPONENTS_CACHE_STR, '01112024');

	const [streamId, setStreamId] = useState<string | null>(null);
	const [endDate, setEndDate] = useState<Date | null>(null);
	const [extensionNotificationDate, setExtensionNotificationDate] = useState<string | null>(null);
	const [meetingLastConnectionDate, setMeetingLastConnectionDate] = useState<string | null>(null);
	const [roomId, setRoomId] = useState<string | null>(null);
	const [socketURL, setSocketURL] = useState<string | null>(null);
	const [providerName, setProviderName] = useState<string | null>(null);
	const [token, setToken] = useState<string | null>(null);
	const [userInfos, setUserInfos] = useState<UserInfosType | null>(null);
	const [isInitCamera, setIsInitCamera] = useState<boolean>();
	const [isWhiteBoard, setIsWhiteBoard] = useState(false);
	const [whiteboardLastData, setWhiteboardLastData] = useState<any>(null);
	const [isOnline, setIsOnline] = useState<boolean>(true);
	const [meetingId, setMeetingId] = useState(null);
	const [isRetrievingInformation, setIsRetrievingInformation] = useState(true);

	const [isConnectedStudent, setIsConnectedStudent] = useState(false);
	const [studentId, setStudentId] = useState<number | null>(null);

	const [questionImages, setQuestionImages] = useState([]);
	const extensionAskedRef = useRef(false);

	const [issueReportEnabled, setIssueReportEnabled] = useState(true);

	const onCameraStatusToggle = () => {
		if (activeVideoCamera) {
			meetWvRef.current?.onCameraStatus(false);
			setActiveVideoCamera(false);
		} else {
			meetWvRef.current?.onCameraStatus(true);
			setActiveVideoCamera(true);
		}
	};

	const onMicrophoneStatusToggle = () => {
		if (activeMicrophone) {
			meetWvRef.current?.onMicrophoneStatus(false);
			setActiveMicrophone(false);
		} else {
			meetWvRef.current?.onMicrophoneStatus(true);
			setActiveMicrophone(true);
		}
	};

	const onViewTypeToggle = () => {
		if (viewType === 1) {
			setViewType(2);
			meetWvRef.current?.onViewTypeChange(2);
		} else {
			setViewType(1);
			meetWvRef.current?.onViewTypeChange(1);
		}
	};

	const onScreenShare = () => {
		if (!activeScreenShare) {
			setActiveScreenShare(true);
			meetWvRef.current?.onSwitchScreenShare();
		} else {
			setActiveScreenShare(false);
			meetWvRef.current?.onSwitchOffScreenShare();
		}
	};

	const onToggleWhiteBoard = () => {
		if (isWhiteBoard) {
			setIsWhiteBoard(false);
			setIsToaster(false);
			meetWvRef.current?.onViewTypeChange(viewType);
		} else {
			setIsToaster(true);
			setIsWhiteBoard(true);
			meetWvRef.current?.onViewTypeChange(3);
		}
	};

	const handleOnline = () => {
		setIsOnline(true);
		getCurrentEndDate();
	};

	const handleOffline = () => {
		setIsOnline(false);
	};

	const onEndDateUpdate = (endDate: Date) => {
		meetWvRef.current?.onEndDateUpdate(endDate);
		setEndDate(endDate);
	};

	const onCancelScreenShare = () => {
		setActiveScreenShare(false);
	};

	const handleEndCall = (isMeetCamera = false, reasonStatus = MEETING_REASON_TYPES.ENDED_BY_EXPERT) => {
		setShowExtensionModal(false);
		setShowEndModal(false);
		onLeaveCall(isMeetCamera, reasonStatus);
	};

	const sendExtensionSubmit = () => {
		onExtensionSubmitted();
		setShowExtensionSubmittedModal(false);
		setShowExtensionModal(false);
		setShowEndModal(false);
	};

	const onShowExtension = (): void => {
		if (connectionApp === 'COACHING' && !extensionAskedRef.current) {
			setShowExtensionModal(true);
		}
	};

	const onToggleMessages = () => {
		setIsActiveMessages(prev => !prev);

		setHasUnreadMessages(false);
	};

	const initAllState = () => {
		setShowExtensionSubmittedModal(false);
		setShowExtensionModal(false);
		setShowEndModal(false);
		setActiveMicrophone(true);
		setActiveVideoCamera(true);
		setWhiteboardLastData(null);
		setIsActiveMessages(false);
		setHasUnreadMessages(false);
		setIncomingMessages([]);
	};

	const handleMessageSent = (content: string) => {
		if (emit && roomId && userInfos?.coachId) {
			const messageForIframe = {
				content: content,
				sender: 'USER',
				roomId: roomId,
				timestamp: new Date().toISOString()
			};

			const messageForSocket = {
				content: content,
				sender: 'COACH',
				roomId: roomId,
				timestamp: new Date().toISOString()
			};

			setIncomingMessages(prev => [...prev, messageForIframe]);

			sendGenericEmit(
				SOCKET_EVENTS.EMIT.VIDEO_CALL_MESSAGE,
				{
					userType: 'STUDENT',
					userId: studentId,
					payload: messageForSocket
				},
				emit
			);
		}
	};

	const initAllListener = () => {
		on(SOCKET_EVENTS.ON.MEETING_EXTENSION_PROPOSED, () => {
			setShowExtensionSubmittedModal(true);
		});

		on(SOCKET_EVENTS.ON.MEETING_EXTENSION_COMPLETED, (data: any) => {
			const dataParsed = jsonTryParse(data);
			onEndDateUpdate(dataParsed?.endDate);
		});

		on(SOCKET_EVENTS.ON.MEETING_ISSUE_CREATED, (data: any) => {
			const dataParsed = jsonTryParse(data);
			const _data = dataParsed?.newVideoCallProviderInfo;

			if (_data?.name === VIDEO_CALL_PROVIDER_NAMES.GOOGLE_MEET || dataParsed?.backupVideoCallProvider === VIDEO_CALL_PROVIDER_NAMES.GOOGLE_MEET) {
				setGoogleMeetProvidedLink(dataParsed);
				setConnectMeetingLinkModal(true);
			} else {
				setToken(dataParsed?.token);
				setStreamId(dataParsed?.providerParticipantId);
				setSocketURL(_data?.host);
				setRoomId(dataParsed?.roomId);
				setProviderName(_data?.name);

				setIsOnline(false);
				setTimeout(() => {
					setIsOnline(true);
				}, TimeSpan.milliSecond.oneHundred);

				setIssueReportEnabled(false);

				setTimeout(() => {
					setIssueReportEnabled(true);
				}, TimeSpan.minutes.one);
			}
		});

		on(SOCKET_EVENTS.ON.STUDENT_JOINED, () => {
			setIsConnectedStudent(true);
			meetWvRef.current?.onOpponentConnected();
			off(SOCKET_EVENTS.ON.STUDENT_JOINED);
		});

		on(SOCKET_EVENTS.ON.VIDEO_CALL_MESSAGE, data => {
			const dataParsed = jsonTryParse(data);

			if (dataParsed?.sender === 'STUDENT') {
				setIncomingMessages(prev => [
					...prev,
					{
						content: dataParsed?.content,
						sender: dataParsed?.sender,
						timestamp: dataParsed?.timestamp
					}
				]);

				if (!hasUnreadMessages) {
					setHasUnreadMessages(true);
				}
			}
		});
	};

	useEffect(() => {
		if (isInitCamera) {
			controlPermission();
		}
	}, [isInitCamera]);

	useEffect(() => {
		const isWebView = checkWebView();
		setIsInWebView(isWebView);
	}, []);

	useEffect(() => {
		if (handleMatch?.attachmentInfos) {
			const imageList = handleMatch?.attachmentInfos?.map((imgData: any) => imgData?.baseUrl + '/' + imgData?.path + '/' + imgData?.filename);
			setQuestionImages(imageList);
		}
	}, [handleMatch?.attachmentInfos]);

	useEffect(() => {
		if (callState === CALL_STATE_TYPES.CONNECT) {
			window.addEventListener('online', handleOnline);
			window.addEventListener('offline', handleOffline);
		}

		return () => {
			window.removeEventListener('online', handleOnline);
			window.removeEventListener('offline', handleOffline);
		};
	}, [callState, meetingId]);

	useEffect(() => {
		if (serverTimeData && callState === CALL_STATE_TYPES.CONNECT && isOnline) {
			meetWvRef?.current?.onServerTimeUpdate?.(serverTimeData?.serverTime?.toString?.());
		}
	}, [serverTimeData, isOnline, callState]);

	useEffect(() => {
		if (dopingTechComponentsCacheStr) {
			dopingTechComponentsCacheStrRef.current = dopingTechComponentsCacheStr;
		}
	}, [dopingTechComponentsCacheStr]);

	const getCurrentEndDate = (id = null): void => {
		setIsRetrievingInformation(true);

		emit(SOCKET_EVENTS.EMIT.MEETING_INFORMATION_REQUESTED, {
			meetingId: id || meetingId
		});

		const serviceTimeOut = setTimeout(() => setIsRetrievingInformation(false), 2000);

		on(SOCKET_EVENTS.ON.MEETING_INFORMATION_RETRIEVED, data => {
			const dataParsed = jsonTryParse(data);

			const studentParticipant = dataParsed?.participantResponseList?.find?.((item: any) => item?.participantType === 'STUDENT');

			setStudentId(studentParticipant?.participantId || null);

			if (studentParticipant?.connectionStatus === 'CONNECTED') {
				setIsConnectedStudent(true);
				meetWvRef.current?.onOpponentConnected();
			}

			if (dataParsed?.endDate) {
				onEndDateUpdate(dataParsed?.endDate);
				setMeetingType(dataParsed?.meetingType);
			}

			if (dataParsed?.isExtendedMeeting) {
				extensionAskedRef.current = true;
			}

			if (dataParsed?.isIssueReported && dataParsed?.backupVideoCallProviderUrl) {
				setGoogleMeetProvidedLink({
					backupVideoCallProviderUrl: dataParsed?.backupVideoCallProviderUrl
				});
				setConnectMeetingLinkModal(true);
			}

			setIsRetrievingInformation(false);
			clearTimeout(serviceTimeOut);

			off(SOCKET_EVENTS.ON.MEETING_INFORMATION_RETRIEVED);
		});
	};

	useImperativeHandle(ref, () => ({
		init: ({ streamId, socketURL, userInfos, token, roomId, meetingId, endDate, extensionNotificationDate, meetingLastConnectionDate, providerName }: MeetCameraInitProps) => {
			setToken(token);
			setStreamId(streamId);
			setSocketURL(socketURL);
			setProviderName(providerName);
			setUserInfos(userInfos);
			setRoomId(roomId);
			setEndDate(endDate);
			setMeetingId(meetingId);
			setExtensionNotificationDate(extensionNotificationDate);
			setMeetingLastConnectionDate(meetingLastConnectionDate);
			initAllState();
			initAllListener();
			setIsInitCamera(true);
			setIsWhiteBoard(false);
			setIsToaster(false);
			setStudentId(null);
			extensionAskedRef.current = false;
			getCurrentEndDate(meetingId);
			setIsConnectedStudent(false);
			setGoogleMeetProvidedLink({});
			setConnectMeetingLinkModal(false);
		},
		onEndDateUpdate: (endDate: Date) => {
			meetWvRef.current?.onEndDateUpdate(endDate);
			setEndDate(endDate);
		}
	}));

	const onToggleCamera = () => {
		meetWvRef.current?.onToggleCameraMobile();
	};

	const onToggleToaster = () => {
		if (isToaster) {
			setIsToaster(false);
			setIsWhiteBoard(false);
			meetWvRef.current?.onViewTypeChange(viewType);
		} else {
			setIsToaster(true);
			meetWvRef.current?.onViewTypeChange(3);
		}
	};

	if (callState !== CALL_STATE_TYPES.CONNECT) {
		return null;
	}

	const handleWhiteboardMessage = (data: any) => {
		if (isEmptyObject(data)) {
			return;
		}

		const parsedData = jsonTryParse(data);
		const _data = {
			type: 'whiteboard',
			data: parsedData
		};

		setWhiteboardLastData(data);

		//OE-10408 data channel error (gerek olmadigi icin gizlendi)
		//meetWvRef?.current?.onDataChannelSend(JSON.stringify(_data));

		sendGenericEmit(
			SOCKET_EVENTS.EMIT.WHITE_BOARD,
			{
				userType: 'STUDENT',
				userId: studentId,
				payload: parsedData
			},
			emit
		);
	};

	const onIssueReportClick = () => {
		if (issueReportEnabled) {
			setIssueReportModal(true);

			emit(SOCKET_EVENTS.EMIT.MEETING_ISSUE_OPTION_REQUESTED, {});

			on(SOCKET_EVENTS.ON.MEETING_ISSUE_OPTION_RETRIEVED, data => {
				const dataParsed = jsonTryParse(data);
				setIssueOptions(dataParsed || []);

				off(SOCKET_EVENTS.ON.MEETING_ISSUE_OPTION_RETRIEVED);
			});
		}
	};

	const onSelectIssueOption = (optionId: any) => {
		setSelectedIssueOptions((prevState: any) => {
			let newSelectedOptions = [...prevState];

			if (newSelectedOptions.includes(optionId)) {
				newSelectedOptions = newSelectedOptions.filter(id => id !== optionId);
			} else {
				newSelectedOptions.push(optionId);
			}

			return newSelectedOptions;
		});
	};

	const onSendIssueReport = () => {
		emit(SOCKET_EVENTS.EMIT.MEETING_ISSUE_REPORTED, {
			meetingId: meetingId,
			issueOptionIdList: selectedIssueOptions
		});

		setIssueReportModal(false);
		setSelectedIssueOptions([]);
	};

	const onGoToGoogleMeet = () => {
		if (googleMeetProvidedLink?.backupVideoCallProviderUrl) {
			if (window?.ReactNativeWebView?.postMessage) {
				sendMessageToWebView({
					type: PostMessage.openExternalLink,
					data: {
						link: googleMeetProvidedLink?.backupVideoCallProviderUrl
					}
				});
			} else {
				window.open(googleMeetProvidedLink?.backupVideoCallProviderUrl, '_blank');
			}
		}
	};

	const onCopyLinkToClipboard = () => {
		if (googleMeetProvidedLink?.backupVideoCallProviderUrl) {
			navigator.clipboard.writeText(googleMeetProvidedLink?.backupVideoCallProviderUrl);
			setCopiedTxtShown(true);

			setTimeout(() => {
				setCopiedTxtShown(false);
			}, 2000);
		}
	};

	return (
		<>
			<div
				className={ClassNames('meet-camera-container', {
					toaster: isToaster
				})}>
				{!isToaster && (
					<MeetCameraHeader
						userInfos={userInfos}
						onToggleCamera={onToggleCamera}
						onLeave={() => setShowEndModal(true)}
					/>
				)}
				{!isOnline || !isConnected || isRetrievingInformation ? (
					<div className="meet-vw-offline">
						<LoadingSpinner loadingText={'İnternet bağlantınız koptu. Tekrar bağlanmaya çalışılıyor'} />
					</div>
				) : (
					<div className="meet-vw-wrapper">
						<MeetWv
							ref={meetWvRef}
							isToaster={isToaster}
							streamId={streamId}
							roomId={roomId}
							socketURL={socketURL}
							providerName={providerName}
							token={token}
							isConnectedStudent={isConnectedStudent}
							onCancelScreenShare={onCancelScreenShare}
							endDate={endDate}
							onEndTime={() => handleEndCall(true, MEETING_REASON_TYPES.EXPIRED)}
							extensionNotificationDate={extensionNotificationDate}
							meetingLastConnectionDate={meetingLastConnectionDate}
							onExtensionNotification={onShowExtension}
							onMeetingLastConnectionNotification={onShowStudentNotConnect}
							onDeviceList={devices => setDeviceList(devices)}
							userInfos={userInfos}
							onInitialized={() => onInitialized(meetingId)}
							isPermissionLoading={!hasPermission}
							activeMicrophone={activeMicrophone}
							activeVideoCamera={activeVideoCamera}
							onMicrophoneSourceChange={deviceId => setActiveMicrophoneDevice(deviceId)}
						/>
						<VideoCallMessages
							isActive={isActiveMessages}
							userInfos={userInfos}
							incomingMessages={incomingMessages}
							onMessageSent={handleMessageSent}
							onToggle={onToggleMessages}
							dopingTechComponentsCacheStr={dopingTechComponentsCacheStrRef?.current}
						/>
					</div>
				)}

				<MeetCameraBottom
					isToaster={isToaster}
					studentId={studentId}
					activeVideoCamera={activeVideoCamera}
					activeMicrophone={activeMicrophone}
					toggleVideoCamera={onCameraStatusToggle}
					toggleMicrophone={onMicrophoneStatusToggle}
					toggleViewType={onViewTypeToggle}
					activeScreenShare={activeScreenShare}
					microphoneDevices={deviceList?.microphones}
					cameraDevices={deviceList?.cameras}
					switchScreenShare={onScreenShare}
					onToggleToaster={onToggleToaster}
					hasUnreadMessages={hasUnreadMessages}
					onLeave={() => setShowEndModal(true)}
					isWhiteBoard={isWhiteBoard}
					toggleWhiteBoard={onToggleWhiteBoard}
					onToggleMessages={onToggleMessages}
					isActiveMessages={isActiveMessages}
					onAudioInputSourceChange={deviceId => meetWvRef?.current?.onAudioInputSourceChange?.(deviceId)}
					onCameraSourceChange={deviceId => meetWvRef?.current?.onCameraSourceChange?.(deviceId)}
					isInWebView={isInWebView}
					activeMicrophoneDevice={activeMicrophoneDevice}
					issueReportEnabled={issueReportEnabled}
					onIssueReportClick={onIssueReportClick}
				/>
			</div>

			{isWhiteBoard && (
				<WhiteBoardWv
					ref={whiteboardRef}
					streamId={streamId}
					whiteboardLastData={whiteboardLastData}
					onWhiteBoardData={data => handleWhiteboardMessage(data)}
				/>
			)}

			{isWhiteBoard && <WhiteboardQuestionGallery images={questionImages} />}

			<DialogModal
				isOpen={showEndModal}
				onClose={() => setShowEndModal(false)}
				variant="waiting"
				title="Emin Misiniz?"
				primaryButtonText="Evet"
				secondaryButtonText={'Hayır'}
				onPrimaryClick={() => handleEndCall(true)}>
				<p>
					Görüşmeden ayrılmak istediğinize
					<b> emin misiniz?</b>
				</p>
			</DialogModal>

			<DialogModal
				isOpen={showExtensionModal}
				onClose={() => setShowExtensionModal(false)}
				variant="waiting"
				title="Süre Azalıyor!"
				primaryButtonText="Evet"
				secondaryButtonText={'Hayır'}
				onPrimaryClick={sendExtensionSubmit}
				shouldCloseOnOverlayClick={false}>
				<p>
					{`Son sözlerinizi söylemek için görüşmenizi `}
					<b>{convertMsToClockTime(meetingCreated?.extensionDuration)?.minutes || 5}</b>
					<b> dakika</b>
					{` daha uzatmak ister misiniz?`}
				</p>
			</DialogModal>

			<DialogModal
				isOpen={showExtensionSubmittedModal}
				onClose={() => setShowExtensionSubmittedModal(false)}
				variant="waiting"
				title="Süre Azalıyor!"
				primaryButtonText="Kabul Ediyorum"
				secondaryButtonText={'Hayır'}
				onPrimaryClick={sendExtensionSubmit}
				shouldCloseOnOverlayClick={false}>
				<p>
					Öğrenciniz görüşmeyi
					<b> 10 dakika</b> daha uzatmak istiyor, kabul ediyor musunuz?
				</p>
			</DialogModal>

			<DialogModal
				isOpen={connectMeetLinkModal}
				onClose={() => null}
				variant="waiting"
				title="Google Meet"
				showCloseIcon={false}
				primaryButtonText="Linke git"
				secondaryButtonActive={false}
				onPrimaryClick={() => {
					onGoToGoogleMeet();
				}}>
				<div className="google-meet-provider-modal-container">
					<p>
						Öğrenciniz görüşme için aşağıdaki linkte sizi bekliyor. Aşağıdaki "Linke git" butonuna tıklayarak Google Meet uygulamasına geçiş yapabilirsiniz yada linki kopyalayarak
						tarayıcınızda açabilirsiniz.
					</p>

					<input
						type="text"
						className="input-google-meet"
						readOnly={true}
						value={googleMeetProvidedLink?.backupVideoCallProviderUrl}
					/>
					{copiedTxtShown ? (
						<div className="copy-link">Kopyalandı!</div>
					) : (
						<div
							className="copy-link"
							onClick={() => onCopyLinkToClipboard()}>
							Linki Kopyala
						</div>
					)}
				</div>
			</DialogModal>

			<DialogModal
				isOpen={issueReportModal}
				onClose={() => setIssueReportModal(false)}
				variant="warning"
				title="Bağlantı Sorunu Bildir"
				primaryButtonText="Devam Et"
				secondaryButtonText="Vazgeç"
				primaryButtonActive={selectedIssueOptions?.length > 0}
				onPrimaryClick={onSendIssueReport}
				shouldCloseOnOverlayClick={false}>
				<div className="issue-report-modal-container">
					<div className="description">Aşağıdaki problemlerden hangisini yaşıyorsun?</div>
					<div className="issue-list">
						{issueOptions?.map?.((item: any) => {
							return (
								<div
									key={`issue-report-item-key${item?.id}`}
									className="issue-item">
									<input
										type="checkbox"
										id={`issue-${item?.id}`}
										checked={selectedIssueOptions?.find((id: any) => id === item?.id)}
										onClick={() => onSelectIssueOption(item?.id)}
									/>
									<label htmlFor={`issue-${item?.id}`}>{item?.name}</label>
								</div>
							);
						})}
					</div>
				</div>
			</DialogModal>
		</>
	);
});

export default MeetCamera;
